import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Client } from '../../../data/models/client.model';
import {
  Program,
  ServicePlan,
  ServicePlanServiceArea,
} from '../../../data/models/client-program.model';
import ServicePlanComponent from './ServicePlanComponent';
import { TransportationBenefit } from '../../../data/models/transportation-benefit.model';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'ProgramList';

const classes = {
  container: `${PREFIX}-container`,
  panelTitle: `${PREFIX}-panelTitle`,
  summary: `${PREFIX}-summary`,
  program: `${PREFIX}-program`,
  servicePlans: `${PREFIX}-servicePlans`,
  highlight: `${PREFIX}-highlight`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(2),
  },

  [`& .${classes.panelTitle}`]: {
    fontWeight: 700,
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  [`& .${classes.program}`]: {
    padding: theme.spacing(2),
  },

  [`& .${classes.servicePlans}`]: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    pointerEvents: 'none',
  },

  [`& .${classes.highlight}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
}));

interface ProgramListProps {
  client: Client;
  programs: Program[];
  servicePlans: ServicePlan[];
  purposeCodes: ClientPurposeCode[];
  serviceAreas: ServicePlanServiceArea[];
  transportationBenefits: TransportationBenefit[];
  onEdit: (evt: any, program: Program) => void;
  onDelete: (evt: any, program: Program) => void;
  onActivate: (evt: any, program: Program) => void;
  onDeactivate: (evt: any, program: Program) => void;
  onDragEnter: (evt: any, program: Program) => void;
  onDragLeave: (evt: any, program: Program) => void;
  onDrop: (program: Program) => void;
  dropProgramUUID?: string;
}

export default function ProgramList({
  client,
  programs,
  servicePlans,
  purposeCodes,
  serviceAreas,
  transportationBenefits,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  onDragEnter,
  onDragLeave,
  onDrop,
  dropProgramUUID,
}: ProgramListProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (evt: any, program: Program) => {
    if (evt.target.checked) {
      onActivate(evt, program);
    } else {
      onDeactivate(evt, program);
    }
  };

  return (
    <StyledBox className={classes.container}>
      {programs.map((program) => {
        const className =
          program.programUUID === dropProgramUUID ? classes.highlight : '';
        return (
          <Accordion key={program.programUUID} className={className}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.summary}>
                <Typography className={classes.panelTitle}>
                  {program.name}
                </Typography>
                <Box>
                  {!isSuperReaderUser && (
                    <Tooltip title={t('client.editProgram')}>
                      <span>
                        <IconButton
                          data-testid="edit-contact"
                          onClick={(evt) => onEdit(evt, program)}
                          color="primary"
                          size="large">
                          <EditOutlinedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {!isSuperReaderUser && (
                    <Tooltip title={t('client.deleteProgram')}>
                      <span>
                        <IconButton
                          data-testid="delete-program"
                          onClick={(evt) => onDelete(evt, program)}
                          color="error"
                          size="large"
                          disabled={program.state === 'active'}>
                          <DeleteForeverIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {!isSuperReaderUser && (
                    <Tooltip
                      title={
                        program.state === 'active'
                          ? t('client.active')
                          : t('client.inactive')
                      }>
                      <Switch
                        checked={program.state === 'active'}
                        onChange={(evt) => handleActiveChange(evt, program)}
                        onClick={(evt) => evt.stopPropagation()}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              onDragOver={(evt) => evt.preventDefault()}
              onDragEnter={(evt) => onDragEnter(evt, program)}
              onDragLeave={(evt) => onDragLeave(evt, program)}
              onDrop={(evt) => onDrop(program)}>
              <Box className={classes.servicePlans}>
                {!program.servicePlans?.length && (
                  <Typography>{t('client.dropServicePlan')}</Typography>
                )}
                {program.servicePlans?.map((servicePlanUUID) => {
                  const servicePlan = servicePlans.find(
                    (sp) => sp.servicePlanUUID === servicePlanUUID,
                  );
                  return servicePlan ? (
                    <ServicePlanComponent
                      key={servicePlan.servicePlanUUID}
                      program={program}
                      servicePlan={servicePlan}
                      purposeCodes={purposeCodes}
                      serviceAreas={serviceAreas}
                      transportationBenefits={transportationBenefits}
                    />
                  ) : null;
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </StyledBox>
  );
}
